import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi, type AdminClient } from '../api/admin';
import {
  clearConfirm, closePlan, editStep, errorCode, errorMessage, getAdminPlan, getAdminState, getBoardSections, getUatSigners, importPlan, openNextRound, previewPurge, previewReplace, previewRestart, publishPlan, purgePlan, restartRound, retireStep, setStatement, setUatSigner, updateRound,
  type ImportRow, type ImportSummary, type Plan, type PlanDetail, type PurgeKind, type PurgePreview, type Readiness, type Step,
} from '../api/uat';
import { readXlsx } from '../lib/xlsxRead';
import { parsePlan } from '../modules/uat/parsePlan';
import { useAdminCtx } from './AdminShell';
import TesterFilters from './TesterFilters';

// Staff management of a client's UAT plans: import from a workbook, edit, clear every CONFIRM,
// publish, and choose where each round's defects land on the board. Reachable by direct URL, so
// the client is fetched and named on screen.

const box: React.CSSProperties = { display: 'grid', gap: 12, background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, padding: 16 };
const input: React.CSSProperties = { border: '1px solid var(--mi-border)', borderRadius: 6, padding: '6px 8px', font: 'inherit', fontSize: 13 };
const secondary: React.CSSProperties = { background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', color: 'var(--mi-text)', padding: '5px 10px', fontSize: 13 };
const muted: React.CSSProperties = { margin: 0, fontSize: 13, color: 'var(--mi-muted)' };
/** The red outline of a button that closes or deletes for good. */
const dangerButton: React.CSSProperties = { ...secondary, borderColor: '#b3261e', color: '#b3261e' };
/** "1 step", "2 steps". */
const n = (k: number, word: string) => `${k} ${word}${k === 1 ? '' : 's'}`;
type Act = (label: string, fn: () => Promise<unknown>, planGone?: boolean) => Promise<void>;
// Stamped when they resolve and skipped when they don't; logging an issue works either way.
const OPTIONAL_CHECKS = new Set(['priority', 'phase']);
const STATUS_WORD: Record<Plan['status'], string> = { draft: 'Draft', published: 'Published', closed: 'Closed' };

function StepEditor({ step, onSave }: { step: Step; onSave(patch: { role: string | null; action: string; expected: string }): Promise<void> }) {
  const [role, setRole] = useState(step.role ?? '');
  const [action, setAction] = useState(step.action);
  const [expected, setExpected] = useState(step.expected);
  const [busy, setBusy] = useState(false);
  const dirty = role !== (step.role ?? '') || action !== step.action || expected !== step.expected;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <input style={input} aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role" />
      <textarea style={{ ...input, minHeight: 60 }} aria-label="Test action" value={action} onChange={(e) => setAction(e.target.value)} />
      <textarea style={{ ...input, minHeight: 60 }} aria-label="Expected result" value={expected} onChange={(e) => setExpected(e.target.value)} />
      <div>
        <button className="btn-primary" disabled={!dirty || busy || !action.trim() || !expected.trim()}
          onClick={async () => { setBusy(true); try { await onSave({ role: role.trim() || null, action: action.trim(), expected: expected.trim() }); } finally { setBusy(false); } }}>
          Save step
        </button>
      </div>
    </div>
  );
}

/** What a permanent delete removes, and the plan's name to be typed back before it is allowed. */
function PurgeConfirm({ kind, pv, onConfirm }: { kind: PurgeKind; pv: PurgePreview; onConfirm(typed: string): void }) {
  const [typed, setTyped] = useState('');
  if (pv.signoffs > 0) {
    return (
      <div role="alert" style={{ color: '#b3261e', fontSize: 13 }}>
        “{pv.plan.name}” has been signed off ({n(pv.signoffs, 'sign-off')}). A sign-off is a permanent record, so neither its results nor the plan can be deleted.
      </div>
    );
  }
  const c = pv.counts;
  const armed = typed === pv.plan.name;
  return (
    <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
      <div>
        {kind === 'plan' ? <>This permanently deletes <b>“{pv.plan.name}”</b>: </> : <>This permanently deletes everything recorded on <b>“{pv.plan.name}”</b>: </>}
        {n(c.results, 'client result')}, {n(c.staffResults, 'MI run')}, {n(c.defects + c.staffDefects, 'issue record')}, {n(c.resets, 'reset')} and {n(c.stepTasks, 'question task record')}
        {kind === 'plan' && <>, and the plan itself: {n(c.steps, 'step')} in {n(c.sections, 'section')} and {n(c.rounds, 'round')}</>}.
        {kind === 'results' && ' The plan, its steps and its rounds stay, with every step back to Not run.'}
      </div>
      {c.asanaTasks > 0 && <div style={muted}>{n(c.asanaTasks, 'Asana task')} created from these stay on the board, and a new issue on one of these steps will open a new task.</div>}
      <label style={{ display: 'grid', gap: 4 }}>Type the plan’s name to confirm
        <input style={{ ...input, maxWidth: 420 }} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={pv.plan.name} />
      </label>
      <div>
        <button style={{ ...dangerButton, ...(armed ? { background: '#b3261e', color: '#fff' } : { opacity: 0.5 }) }} disabled={!armed} onClick={() => onConfirm(typed)}>
          {kind === 'plan' ? 'Permanently delete this plan' : 'Permanently delete these results'}
        </button>
      </div>
    </div>
  );
}

/**
 * Restart a round (nothing deleted), and, for administrators, the permanent deletes. Mount it with
 * `key={plan.id}`: everything it holds belongs to one plan, so a new plan starts it afresh.
 */
function ClearPanel({ clientId, plan, hasOpenRound, isAdmin, getToken, act }: {
  clientId: string; plan: Plan; hasOpenRound: boolean; isAdmin: boolean; getToken: () => Promise<string | null>; act: Act;
}) {
  const [restart, setRestart] = useState<{ round: { number: number; name: string }; steps: number } | null>(null);
  const [reason, setReason] = useState('');
  const [danger, setDanger] = useState<{ kind: PurgeKind; pv: PurgePreview } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const look = async (fn: () => Promise<void>) => { setError(null); try { await fn(); } catch (e) { setError(errorMessage(e)); } };

  return (
    <section style={box} aria-label="Clear results or delete this plan">
      <h3 style={{ margin: 0, fontSize: 15 }}>Clear results or delete this plan</h3>
      <p style={muted}>Tasks that issues and questions already created on the client’s Asana board are never touched by either. Close or delete them there.</p>

      {plan.status === 'published' && hasOpenRound && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={secondary} onClick={() => look(async () => setRestart(await previewRestart(clientId, plan.id, await getToken())))}>Restart the open round…</button>
            <span style={muted}>Puts every step that has a result back to Not run. Nothing is deleted: the history stays in Download results.</span>
          </div>
          {restart && (
            <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
              <div>{restart.steps === 0
                ? `${restart.round.name} has no step with a result to restart.`
                : <>Restarting {restart.round.name} puts <b>{restart.steps}</b> step{restart.steps === 1 ? '' : 's'} back to Not run.</>}</div>
              {restart.steps > 0 && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input style={{ ...input, minWidth: 260 }} aria-label="Why restart" placeholder="Why (optional), for example: tested against the wrong data set" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <button className="btn-primary" onClick={() => {
                    const steps = n(restart.steps, 'step');
                    if (!window.confirm(`Restart ${restart.round.name}? ${steps} go back to Not run. Nothing is deleted.`)) return;
                    act(`${restart.round.name} restarted: ${steps} ${restart.steps === 1 ? 'is' : 'are'} back to Not run.`, async () => {
                      await restartRound(clientId, plan.id, reason.trim() || null, await getToken());
                      setRestart(null); setReason('');
                    });
                  }}>Restart {restart.round.name}</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isAdmin ? (
        <div style={{ display: 'grid', gap: 8, borderTop: '1px solid var(--mi-border)', paddingTop: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {(['results', 'plan'] as const).map((kind) => (
              <button key={kind} style={dangerButton} onClick={() => look(async () => setDanger({ kind, pv: await previewPurge(clientId, plan.id, kind, await getToken()) }))}>
                {kind === 'results' ? 'Delete results…' : 'Delete plan…'}
              </button>
            ))}
            <span style={muted}>Permanent, and shown to you first. Neither works on a plan that has been signed off.</span>
          </div>
          {/* Keyed by kind, so the name typed for one delete is not carried to the other. */}
          {danger && (
            <PurgeConfirm key={danger.kind} kind={danger.kind} pv={danger.pv} onConfirm={(typed) => act(
              danger.kind === 'plan' ? `“${danger.pv.plan.name}” deleted.` : `The results on “${danger.pv.plan.name}” are deleted.`,
              async () => { await purgePlan(clientId, danger.pv.plan.id, danger.kind, typed, await getToken()); setDanger(null); },
              danger.kind === 'plan',
            )} />
          )}
        </div>
      ) : (
        <p style={{ ...muted, borderTop: '1px solid var(--mi-border)', paddingTop: 10 }}>Deleting results or a plan permanently is for administrators.</p>
      )}
      {error && <p role="alert" style={{ margin: 0, fontSize: 13, color: '#b3261e' }}>{error}</p>}
    </section>
  );
}

export default function AdminModuleUat() {
  const { clientId } = useParams();
  // AuthContext recreates getToken whenever its provider re-renders; a ref keeps that from
  // reloading the page's data (and re-running an import preview) underneath the user.
  const auth = useAuth();
  const tokenRef = useRef(auth.getToken);
  tokenRef.current = auth.getToken;
  const getToken = useCallback(() => tokenRef.current(), []);
  const [client, setClient] = useState<AdminClient | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PlanDetail | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [parsed, setParsed] = useState<{ file: string; rows: ImportRow[]; sheets: string[]; skipped: string[]; errors: string[]; warnings: string[] } | null>(null);
  const [preview, setPreview] = useState<ImportSummary | null>(null);
  const [importInto, setImportInto] = useState<string | null>(null);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  // The permanent deletes are for administrators. The server enforces that; the page only keeps the
  // controls from people who would be refused.
  const { staffRole } = useAdminCtx();
  const isAdmin = staffRole === 'admin';
  // The published plan to close when the draft on screen is published: a new version replacing an old one.
  const [replaceId, setReplaceId] = useState<string | null>(null);
  // How the draft compares with the plan it would replace, and whether the admin has accepted that
  // steps are dropped. Both reset whenever the comparison is taken again.
  const [diff, setDiff] = useState<Awaited<ReturnType<typeof previewReplace>> | null>(null);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [ackRemoved, setAckRemoved] = useState(false);

  const [confirmNotes, setConfirmNotes] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [sections, setSections] = useState<{ gid: string; name: string }[] | null>(null);
  const [intake, setIntake] = useState<string | null>(null);
  const [nextRound, setNextRound] = useState<{ number?: number; retest: number; carried: number } | null>(null);
  const [statement, setStatementText] = useState('');
  const [signerState, setSignerState] = useState<Awaited<ReturnType<typeof getUatSigners>> | null>(null);
  const [roles, setRoles] = useState<Record<string, string>>({});

  useEffect(() => { setStatementText(detail?.plan.signoffStatement ?? ''); }, [detail?.plan.id, detail?.plan.signoffStatement]);
  const loadSigners = useCallback(async () => {
    if (!clientId) return;
    const s = await getUatSigners(clientId, await getToken());
    setSignerState(s);
    setRoles(Object.fromEntries(s.signers.map((x) => [x.userId, x.roleLabel])));
  }, [clientId, getToken]);
  useEffect(() => { loadSigners().catch(() => setSignerState(null)); }, [loadSigners]);

  const reload = useCallback(async () => {
    if (!clientId) return;
    const t = await getToken();
    const [s, c] = await Promise.all([getAdminState(clientId, t), adminApi.getClient(t, clientId)]);
    setPlans(s.plans);
    setReadiness(s.readiness);
    setClient(c);
    setPlanId((cur) => cur ?? s.plans[0]?.id ?? null);
  }, [clientId, getToken]);
  useEffect(() => { reload().catch((e) => setStatus(errorMessage(e))); }, [reload]);

  const loadPlan = useCallback(async () => {
    if (!clientId || !planId) { setDetail(null); return; }
    setDetail(await getAdminPlan(clientId, planId, await getToken()));
  }, [clientId, planId, getToken]);
  useEffect(() => { loadPlan().catch((e) => setStatus(errorMessage(e))); }, [loadPlan]);
  // A choice made for one draft must not carry over to another plan, or to the same one once published.
  useEffect(() => { setReplaceId(null); }, [planId, detail?.plan.status]);

  useEffect(() => {
    if (!clientId || detail?.plan.status !== 'published' || sections) return;
    (async () => {
      const r = await getBoardSections(clientId, await getToken());
      setSections(r.sections);
      setIntake(r.intakeSectionGid);
    })().catch(() => setSections([]));
  }, [clientId, detail, sections, getToken]);

  // Runs `fn`, reloads, and says `label`. When `fn` deleted the plan on screen, the plan is not re-read:
  // the selection is cleared and the list picks the next one.
  const act: Act = async (label, fn, planGone = false) => {
    setStatus(null);
    try {
      await fn();
      if (planGone) { setPlanId(null); setDetail(null); await reload(); } else await Promise.all([reload(), loadPlan()]);
      setStatus(label);
    } catch (e) { setStatus(`${errorMessage(e)}`); }
  };

  const readFile = async (file: File) => {
    setParsed(null); setImportStatus(null);
    try {
      const p = parsePlan(await readXlsx(await file.arrayBuffer()));
      if (!p) { setImportStatus('No sheet in that workbook has “Test ID” and “Expected Result” columns.'); return; }
      setParsed({ file: file.name, ...p });
      if (!newName && !importInto) setNewName(file.name.replace(/\.xlsx$/i, '').replace(/[_-]+/g, ' '));
    } catch (e) { setImportStatus(`Could not read it: ${errorMessage(e)}`); }
  };

  // The preview's new/updated counts depend on the target, so it is re-run whenever the workbook
  // or the "Into" choice changes, and the parsed workbook survives a change of target. Only the
  // latest request may land: a preview for a target no longer chosen would enable Import on it.
  useEffect(() => {
    setPreview(null);
    // A workbook with nothing readable has no rows to send; its sheet errors are shown as they are.
    if (!parsed || !clientId || !parsed.rows.length) return;
    let live = true;
    (async () => {
      // The name is not used by a dry run; the file name satisfies "a new plan needs a name".
      const summary = await importPlan(clientId, { planId: importInto, name: importInto ? null : parsed.file, source: parsed.file, dryRun: true, rows: parsed.rows, sheetErrors: parsed.errors }, await getToken());
      if (live) setPreview(summary);
    })().catch((e) => { if (live) setImportStatus(`Could not preview it: ${errorMessage(e)}`); });
    return () => { live = false; };
  }, [clientId, parsed, importInto, getToken]);

  const applyImport = async () => {
    if (!parsed) return;
    setImportStatus(null);
    try {
      const r = await importPlan(clientId!, { planId: importInto, name: importInto ? null : newName.trim() || parsed.file, source: parsed.file, dryRun: false, rows: parsed.rows, sheetErrors: parsed.errors }, await getToken());
      setImportStatus(`Imported ${r.steps} steps into ${importInto ? 'the draft' : `“${newName.trim() || parsed.file}”`}.`);
      setParsed(null);
      await reload();
      // Re-importing into the plan already on screen leaves planId unchanged, so the effect that
      // loads the plan would not fire and the Steps list would keep the pre-import text.
      const target = r.planId ?? importInto;
      if (target && target === planId) await loadPlan();
      else if (target) setPlanId(target);
    } catch (e) { setImportStatus(`Not imported: ${errorMessage(e)}`); }
  };

  const plan = detail?.plan;
  const steps = detail?.steps ?? [];
  const open = steps.filter((s) => !s.retiredAt && s.confirmNote && !s.confirmClearedAt);
  const sectionTitle = new Map(detail?.sections.map((s) => [s.id, `${s.code}. ${s.title}`]) ?? []);
  const drafts = plans.filter((p) => p.status === 'draft');
  // Only a published plan can be replaced: a draft was never live, and a closed plan is already out of the way.
  const replaceable = plans.filter((p) => p.status === 'published' && p.id !== plan?.id);
  const replacing = replaceable.find((p) => p.id === replaceId);
  const draftId = plan?.status === 'draft' ? plan.id : null;
  useEffect(() => {
    setDiff(null); setDiffError(null); setAckRemoved(false);
    if (!clientId || !draftId || !replacing) return;
    let live = true;
    (async () => {
      const d = await previewReplace(clientId, draftId, replacing.id, await getToken());
      if (live) setDiff(d);
    })().catch((e) => { if (live) setDiffError(errorMessage(e)); });
    return () => { live = false; };
    // `detail` is reloaded after every edit to the draft, so the comparison is never stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, draftId, replacing?.id, detail, getToken]);
  const ids = (list: string[], max = 20) => (list.length > max ? `${list.slice(0, max).join(', ')} and ${list.length - max} more` : list.join(', '));
  const dropped = diff?.removed.length ?? 0;
  const publishBlocked = open.length > 0 || (!!replacing && (!diff || (dropped > 0 && !ackRemoved)));

  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 1000 }}>
      <div>
        <Link to={`/admin/clients/${clientId}`} style={{ color: 'var(--mi-purple)', fontSize: 13 }}>← {client?.name ?? 'Client'}</Link>
        <h2 style={{ margin: '6px 0 0', color: 'var(--mi-navy)' }}>UAT: {client?.name ?? '…'}</h2>
        <p style={muted}>
          Testers work through the published plan in the portal. A failed step becomes a Defect on the client’s delivery board.
          {client && <> Open the module: <Link to={`/${client.slug}/uat`} style={{ color: 'var(--mi-purple)' }}>/{client.slug}/uat</Link>.</>}
        </p>
      </div>

      {readiness && (
        <section style={box}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Readiness: {readiness.ready ? 'issues can be logged' : 'issues cannot be logged yet'}</h3>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: 'grid', gap: 4 }}>
            {readiness.checks.map((c) => {
              const optional = OPTIONAL_CHECKS.has(c.key);
              return (
                <li key={c.key} style={{ color: c.ok ? 'inherit' : optional ? '#8a6212' : '#b3261e' }}>
                  {c.ok ? '✓' : optional ? '!' : '✗'} {c.label}{!c.ok && optional ? ' (optional: defects are logged without this stamp)' : ''}
                </li>
              );
            })}
          </ul>
          {!readiness.ready && <p style={muted}>Testers can record results, but logging an issue fails until these pass. The board is configured on the Asana board screen.</p>}
        </section>
      )}

      <section style={box}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Import a plan</h3>
        <p style={muted}>Reads either layout: a sheet per section with a “Test ID” header row, or the ClientVerse template’s single “Test Cases” sheet. Result columns are ignored. Every <code>[CONFIRM …]</code> becomes a note only staff see, and must be cleared before publishing. To load a new version of a plan that is already published, import it as a new plan; when you publish it you can choose the earlier plan to close.</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>Into
            <select style={input} value={importInto ?? ''} onChange={(e) => { setImportInto(e.target.value || null); setImportStatus(null); }}>
              <option value="">A new plan</option>
              {drafts.map((p) => <option key={p.id} value={p.id}>Draft: {p.name}</option>)}
            </select>
          </label>
          {!importInto && (
            <label style={{ display: 'grid', gap: 4, fontSize: 13, flex: 1, minWidth: 220 }}>Plan name
              <input style={input} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Wave 1: Company, Contact, Campaign" />
            </label>
          )}
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>Workbook (.xlsx)
            <input type="file" accept=".xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ''; }} />
          </label>
          {parsed && !preview && !importStatus && <span style={{ fontSize: 13, color: 'var(--mi-muted)' }}>Previewing {parsed.file}…</span>}
        </div>
        {parsed && !parsed.rows.length && (
          <div style={{ display: 'grid', gap: 6, fontSize: 13, color: '#b3261e' }}>
            <b>{parsed.file}: nothing could be read.</b>
            {parsed.errors.map((e) => <div key={e}>{e}</div>)}
          </div>
        )}
        {preview && parsed && (
          <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
            <div><b>{parsed.file}</b>: {preview.sections} sections, {preview.steps} steps ({preview.added} new, {preview.updated} updated), <b>{preview.confirmCount}</b> with a CONFIRM.</div>
            <div style={{ color: 'var(--mi-muted)' }}>Read {parsed.sheets.join(', ')}{parsed.skipped.length ? `; skipped ${parsed.skipped.join(', ')}` : ''}.</div>
            {preview.padded.length > 0 && <div>Test IDs restored from numeric cells: {preview.padded.map((p) => `${p.from} → ${p.to}`).join(', ')}.</div>}
            {preview.missing.length > 0 && <div>In the draft but not in this file (left alone; retire them if they should go): {preview.missing.join(', ')}.</div>}
            {preview.errors.length > 0 && (
              <div style={{ color: '#b3261e', display: 'grid', gap: 4 }}>
                <b>Fix these in the workbook first:</b>
                {preview.errors.map((e) => <div key={e}>{e}</div>)}
              </div>
            )}
            {parsed.warnings.length > 0 && (
              <div style={{ color: '#8a6212', display: 'grid', gap: 4 }}>
                <b>Worth a look (these don’t stop the import):</b>
                {parsed.warnings.map((w) => <div key={w}>{w}</div>)}
              </div>
            )}
            <div><button className="btn-primary" disabled={preview.errors.length > 0 || (!importInto && !newName.trim())} onClick={applyImport}>Import</button></div>
          </div>
        )}
        {importStatus && <p style={{ margin: 0, fontSize: 13 }}>{importStatus}</p>}
      </section>

      <section style={box}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Plan</h3>
          <select style={input} value={planId ?? ''} onChange={(e) => { setPlanId(e.target.value || null); setSections(null); }}>
            {plans.length === 0 && <option value="">No plans yet</option>}
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name} ({STATUS_WORD[p.status]})</option>)}
          </select>
          {plan && <span style={{ fontSize: 13, color: 'var(--mi-muted)' }}>{STATUS_WORD[plan.status]}{plan.sourceFilename ? ` · from ${plan.sourceFilename}` : ''} · {steps.filter((s) => !s.retiredAt).length} live steps</span>}
        </div>

        {plan?.status === 'draft' && (
          <div style={{ display: 'grid', gap: 10 }}>
            {replaceable.length > 0 && (
              <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>This replaces an earlier version
                <select style={input} value={replacing?.id ?? ''} onChange={(e) => setReplaceId(e.target.value || null)}>
                  <option value="">Nothing: keep {replaceable.length === 1 ? 'the published plan' : 'every published plan'} open</option>
                  {replaceable.map((p) => <option key={p.id} value={p.id}>Close “{p.name}” when this is published</option>)}
                </select>
              </label>
            )}
            {replacing && (
              <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
                <p style={muted}>
                  “{replacing.name}” stops taking results the moment this plan is published, and cannot be reopened. Its results, issues and sign-offs are kept and stay readable.
                </p>
                {diffError && <div style={{ color: '#b3261e' }}>Could not compare it with “{replacing.name}”, so it can’t replace it yet: {diffError}</div>}
                {!diff && !diffError && <span style={{ color: 'var(--mi-muted)' }}>Comparing with “{replacing.name}”…</span>}
                {diff && (
                  <>
                    <div>
                      Compared with “{diff.replaces.name}”: <b>{diff.added.length}</b> added, <b>{dropped}</b> dropped, <b>{diff.changed.length}</b> changed, {diff.unchanged} unchanged.
                    </div>
                    {diff.added.length > 0 && <div style={{ color: 'var(--mi-muted)' }}>Added: {ids(diff.added)}.</div>}
                    {diff.changed.length > 0 && <div style={{ color: 'var(--mi-muted)' }}>Changed: {ids(diff.changed)}.</div>}
                    {dropped > 0 && (
                      <>
                        <div style={{ color: '#b3261e' }}>Dropped: {ids(diff.removed)}. Nobody will test {dropped === 1 ? 'this step' : 'these steps'} once “{diff.replaces.name}” closes.</div>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input type="checkbox" checked={ackRemoved} onChange={(e) => setAckRemoved(e.target.checked)} />
                          I know {dropped} step{dropped === 1 ? ' is' : 's are'} in “{diff.replaces.name}” but not in this plan.
                        </label>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn-primary" disabled={publishBlocked} style={{ opacity: publishBlocked ? 0.45 : 1, cursor: publishBlocked ? 'not-allowed' : 'pointer' }}
                onClick={() => {
                  if (replacing && !window.confirm(`Publish “${plan.name}” and close “${replacing.name}”? Testers can no longer record results on “${replacing.name}”, and it cannot be reopened. Everything already recorded on it is kept.`)) return;
                  act(replacing ? `Published. Round 1 is open, and “${replacing.name}” is closed.` : 'Published. Round 1 is open.', async () => {
                    try { await publishPlan(clientId!, plan.id, await getToken(), replacing?.id ?? null, ackRemoved); } catch (e) {
                      if (errorCode(e) === 'confirm_open') throw new Error('Clear every CONFIRM first.');
                      if (errorCode(e) === 'removed_steps_unacknowledged') throw new Error('The dropped steps changed since you looked. Nothing was published; review the comparison again.');
                      if (errorCode(e) === 'replace_invalid') throw new Error(`“${replacing?.name ?? 'The plan to replace'}” is no longer published, so nothing was changed. Reload and choose again.`);
                      throw e;
                    }
                  });
                }}>
                Publish and open Round 1
              </button>
              <span style={{ fontSize: 13, color: open.length ? '#b3261e' : 'var(--mi-muted)' }}>
                {open.length ? `${open.length} CONFIRM${open.length === 1 ? '' : 's'} still open.` : 'Every CONFIRM is cleared. Publishing shows the plan to the client’s testers.'}
              </span>
            </div>
          </div>
        )}
        {plan?.status === 'published' && (
          <div style={{ display: 'grid', gap: 10 }}>
            {detail!.rounds.map((r) => (
              <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                <b>{r.name}</b>{r.closedAt && <span style={{ color: 'var(--mi-muted)' }}>(closed)</span>}
                <span style={{ color: 'var(--mi-muted)' }}>{r.closedAt ? 'defects landed in' : 'defects land in'}</span>
                <select style={input} value={r.asanaSectionGid ?? ''} disabled={!sections || !!r.closedAt}
                  onChange={(e) => act('Round updated.', async () => updateRound(clientId!, r.id, { name: r.name, asanaSectionGid: e.target.value || null }, await getToken()))}>
                  <option value="">The board’s intake section{intake && sections ? ` (${sections.find((s) => s.gid === intake)?.name ?? intake})` : ''}</option>
                  {(sections ?? []).map((s) => <option key={s.gid} value={s.gid}>{s.name}</option>)}
                </select>
              </div>
            ))}
            <div style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--mi-border)', paddingTop: 10 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                <button style={secondary} onClick={async () => {
                  setStatus(null);
                  try { setNextRound(await openNextRound(clientId!, plan.id, { dryRun: true }, await getToken())); } catch (e) { setStatus(errorMessage(e)); }
                }}>Plan the next round</button>
                {nextRound && (
                  <>
                    <span>Round {nextRound.number} would retest <b>{nextRound.retest}</b> step{nextRound.retest === 1 ? '' : 's'} (failed, changed or not yet run); <b>{nextRound.carried}</b> passed and carry forward.</span>
                    <button className="btn-primary" disabled={!nextRound.retest}
                      onClick={() => {
                        if (!window.confirm(`Open Round ${nextRound.number}? The current round closes and takes no more results.`)) return;
                        act(`Round ${nextRound.number} is open.`, async () => { await openNextRound(clientId!, plan.id, { dryRun: false }, await getToken()); setNextRound(null); });
                      }}>
                      Open Round {nextRound.number}
                    </button>
                  </>
                )}
              </div>
              <p style={muted}>Testers see only the steps being retested; the rest show as passed in an earlier round. Choose where the new round’s issues land in the list above once it opens.</p>
            </div>
            <div>
              <button style={dangerButton}
                onClick={() => { if (window.confirm(`Close “${plan.name}”? Testers will no longer be able to record results. Results already recorded are kept.`)) act('Plan closed.', async () => closePlan(clientId!, plan.id, await getToken())); }}>
                Close plan
              </button>
            </div>
          </div>
        )}
        {status && <p style={{ margin: 0, fontSize: 13 }}>{status}</p>}
      </section>

      {client && <TesterFilters clientId={clientId!} current={client.uat_client_filters ?? []} getToken={getToken} act={act} />}

      {plan && <ClearPanel key={plan.id} clientId={clientId!} plan={plan} hasOpenRound={!!detail?.rounds.some((r) => !r.closedAt)} isAdmin={isAdmin} getToken={getToken} act={act} />}

      {plan && plan.status !== 'draft' && (
        <section style={box}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Final sign-off</h3>
          <p style={muted}>
            A designated client signer agrees to this statement for the plan. The record keeps the exact text, coverage at the time, and every issue still open as an accepted risk. Staff can never sign.
          </p>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>Statement
            <textarea style={{ ...input, minHeight: 70 }} value={statement} onChange={(e) => setStatementText(e.target.value)}
              placeholder="We accept the Wave 1 UAT of ClientVerse for go-live, subject to the open issues listed." />
          </label>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn-primary" disabled={statement.trim() === (plan.signoffStatement ?? '')}
              onClick={() => act('Statement saved.', async () => setStatement(clientId!, plan.id, statement.trim() || null, await getToken()))}>
              Save statement
            </button>
            <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>Version {plan.signoffStatementVersion ?? 0}. Changing the wording starts a new version.</span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6 }}>Signers</div>
          {!signerState ? <p style={muted}>Loading…</p> : signerState.clientUsers.length === 0 ? <p style={muted}>This client has no users yet. Invite them first.</p> : (
            <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {signerState.clientUsers.map((u) => {
                  const current = signerState.signers.find((x) => x.userId === u.userId);
                  return (
                    <tr key={u.userId} style={{ borderTop: '1px solid var(--mi-border)' }}>
                      <td style={{ padding: 6 }}>{u.name ?? u.email}<div style={{ color: 'var(--mi-muted)', fontSize: 12 }}>{u.email}</div></td>
                      <td style={{ padding: 6 }}><input style={input} placeholder="Signs as, e.g. Head of BD&C" value={roles[u.userId] ?? ''} onChange={(e) => setRoles({ ...roles, [u.userId]: e.target.value })} /></td>
                      <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                        <button style={secondary} disabled={!(roles[u.userId] ?? '').trim() || (roles[u.userId] ?? '').trim() === current?.roleLabel}
                          onClick={() => act('Signer saved.', async () => { await setUatSigner(clientId!, u.userId, roles[u.userId].trim(), await getToken()); await loadSigners(); })}>
                          {current ? 'Update' : 'Make signer'}
                        </button>{' '}
                        {current && <button style={secondary} onClick={() => act('Signer removed.', async () => { await setUatSigner(clientId!, u.userId, null, await getToken()); await loadSigners(); })}>Remove</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}

      {open.length > 0 && (
        <section style={box}>
          <h3 style={{ margin: 0, fontSize: 15 }}>CONFIRM queue ({open.length})</h3>
          <p style={muted}>Each note is an open question from the plan’s authors. Clearing one says the step is right to test as written; edit the step first if it isn’t.</p>
          {open.map((s) => (
            <div key={s.id} style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--mi-border)', paddingTop: 10 }}>
              <div style={{ fontSize: 13 }}><b>{s.testId}</b> <span style={{ color: 'var(--mi-muted)' }}>{sectionTitle.get(s.sectionId)}</span></div>
              <div style={{ fontSize: 13, background: '#fbf1dc', color: '#8a6212', borderRadius: 6, padding: '6px 10px', whiteSpace: 'pre-wrap' }}>{s.confirmNote}</div>
              <div style={{ fontSize: 13 }}><span style={{ color: 'var(--mi-muted)' }}>Expect:</span> {s.expected}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...input, flex: 1 }} placeholder="How it was confirmed (e.g. SSO confirmed with Kate, 25 Sep)" value={confirmNotes[s.id] ?? ''} onChange={(e) => setConfirmNotes({ ...confirmNotes, [s.id]: e.target.value })} />
                <button className="btn-primary" disabled={!confirmNotes[s.id]?.trim()} onClick={() => act(`Cleared ${s.testId}.`, async () => clearConfirm(clientId!, s.id, confirmNotes[s.id].trim(), await getToken()))}>Clear</button>
                <button style={secondary} onClick={() => setEditing(s.id)}>Edit step</button>
              </div>
            </div>
          ))}
        </section>
      )}

      {detail && steps.length > 0 && (
        <section style={box}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Steps</h3>
          {plan?.status === 'published' && <p style={muted}>Editing a published step starts a new revision; anyone who tested the old text sees the step as changed and tests it again.</p>}
          <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--mi-muted)' }}><th style={{ padding: 4 }}>ID</th><th style={{ padding: 4 }}>Step</th><th style={{ padding: 4 }} /></tr></thead>
            <tbody>
              {steps.map((s) => (
                <tr key={s.id} style={{ borderTop: '1px solid var(--mi-border)', verticalAlign: 'top', opacity: s.retiredAt ? 0.5 : 1 }}>
                  <td style={{ padding: 6, whiteSpace: 'nowrap', fontWeight: 600 }}>{s.testId}{s.revision > 1 && <div style={{ fontWeight: 400, fontSize: 11, color: 'var(--mi-muted)' }}>rev {s.revision}</div>}</td>
                  <td style={{ padding: 6 }}>
                    {editing === s.id ? (
                      <StepEditor step={s} onSave={async (patch) => { await act(`Saved ${s.testId}.`, async () => editStep(clientId!, s.id, patch, await getToken())); setEditing(null); }} />
                    ) : (
                      <div style={{ display: 'grid', gap: 2 }}>
                        <span style={{ color: 'var(--mi-muted)', fontSize: 12 }}>{sectionTitle.get(s.sectionId)}{s.role ? ` · ${s.role}` : ''}{s.retiredAt ? ' · retired' : ''}{s.confirmClearedAt ? ` · CONFIRM cleared by ${s.confirmClearedBy ?? 'staff'}: ${s.confirmClearedNote}` : ''}</span>
                        <span>{s.action}</span>
                        <span style={{ color: 'var(--mi-muted)' }}>Expect: {s.expected}</span>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                    {plan?.status !== 'closed' && (
                      <>
                        <button style={secondary} onClick={() => setEditing(editing === s.id ? null : s.id)}>{editing === s.id ? 'Cancel' : 'Edit'}</button>{' '}
                        <button style={secondary} onClick={() => act(s.retiredAt ? `Restored ${s.testId}.` : `Retired ${s.testId}.`, async () => retireStep(clientId!, s.id, !s.retiredAt, await getToken()))}>{s.retiredAt ? 'Restore' : 'Retire'}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
